import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { BasicSelectProps, SelectExtendedProps } from 'grommet';
import { observer } from 'mobx-react-lite';
import { ApiError } from '/src/api';
import { BoxProps, Select } from '/src/components';
import { useClientStore, useGlobalStore, useUserStore } from '/src/context';
import { TClientId, TProgramId, TReportingPeriodTypeId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { DateString, ReportingPeriodType, SelectOptions } from '/src/lib/types';
import { getPeriodFromDateString, sortPeriodOptions } from '/src/utils';

export type FirstActivePeriodSelectProps = {
  value: DateString;
  setValue: (reportingPeriod: DateString) => void;
  clientId?: TClientId;
  reportingPeriodTypeId?: TReportingPeriodTypeId;
  firstActivePeriod?: DateString;
  error?: string;
  hideOptionalText?: boolean;
  hideLabel?: boolean;
  label?: string;
  id?: string;
  name?: string;
  disabled?: BasicSelectProps['disabled'];
  placeholder?: string;
  required?: boolean;
  fill?: BoxProps['fill'];
  width?: BoxProps['width'];
  style?: SelectExtendedProps['style'];
  programId?: TProgramId;
  flex?: boolean | BoxProps['flex'];
};

export const FirstActivePeriodSelect: React.FC<FirstActivePeriodSelectProps> = observer((props) => {
  const {
    value,
    setValue,
    clientId,
    reportingPeriodTypeId,
    firstActivePeriod,
    id,
    name,
    label,
    disabled,
    placeholder,
    programId,
    ...selectProps
  } = props;

  /** Context **/
  const globalStore = useGlobalStore();
  const userStore = useUserStore();
  const clientStore = useClientStore();

  /** State **/
  const [options, setOptions] = useState<SelectOptions<DateString>>();

  /** Computed **/
  const isLoading = !options;

  /** Methods **/
  const fetchOptions = async (
    clientId: TClientId,
    reportingPeriodTypeId: TReportingPeriodTypeId,
    firstActivePeriod: DateString
  ) => {
    try {
      /*
        Rules - First Reporting Period

        1. Reporting periods should only be the client's unfinalized reporting periods
        2. Do not allow client users to add facilities/equipment with a locked first reporting period
      */

      const reportingPeriods = await clientStore.fetchClientReportingPeriods(
        clientId,
        reportingPeriodTypeId,
        false, // hide finalized
        userStore.isExternalUser, // hide locked if external
        programId
      );

      const isUSProgram = programId === 1 || programId === 2 || programId === 3;

      const options = reportingPeriods
        .filter((p) => !dayjs(p.start_reporting_quarter).isBefore(firstActivePeriod))
        .filter((p) => {
          if (userStore.isFuseAdmin) {
            return true;
          }

          if (isUSProgram) {
            const periodDate = dayjs(p.start_reporting_quarter);
            const isQ2_2025 = periodDate.year() === 2025 && periodDate.month() >= 3 && periodDate.month() <= 5;
            return !isQ2_2025;
          }
          return true;
        })
        .map((p) => ({
          label: getPeriodFromDateString(
            p.start_reporting_quarter,
            reportingPeriodTypeId === ReportingPeriodType.Yearly
          ),
          value: p.start_reporting_quarter,
        }));

      setOptions(sortPeriodOptions(options));
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.listClientReportingPeriods.error);
    }
  };

  /** Effects **/
  useEffect(() => {
    if (clientId && reportingPeriodTypeId && firstActivePeriod && programId) {
      setOptions(undefined);
      fetchOptions(clientId, reportingPeriodTypeId, firstActivePeriod);
    } else if (options) {
      setOptions(undefined);
    }
  }, [clientId, reportingPeriodTypeId, firstActivePeriod, programId]);

  return (
    <Select
      value={value}
      setValue={(value) => setValue(value)}
      options={options ?? []}
      name={id ? undefined : name || 'reporting_period_select'}
      label={label ?? 'Reporting Period'}
      placeholder={!programId ? 'Select Program First...' : isLoading ? 'Loading...' : placeholder}
      disabled={isLoading || disabled}
      {...selectProps}
    />
  );
});
